import { addDays, differenceInCalendarDays, isBefore, min, startOfDay, subDays } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { formatRangeLabel, formatWindow } from 'features/usage/utils/usage-format';

const arrowClass =
  'flex size-9 items-center justify-center rounded-card text-grey-600 hover:bg-grey-100 disabled:pointer-events-none disabled:text-grey-200 dark:text-muted-foreground dark:hover:bg-accent dark:disabled:text-grey-700';

/** Figma date picker (10597:138): arrows step by the selected range's length; the label opens the calendar. */
export function UsageDateRangePicker({
  periodStart,
  periodEnd,
  selectedRange,
  initialFrom,
  today,
  dayCount,
  onRangeSelect,
  onResetRange,
}: {
  periodStart?: string;
  periodEnd?: string;
  selectedRange: DateRange | undefined;
  initialFrom?: Date;
  today: Date;
  dayCount: number;
  onRangeSelect: (range: DateRange | undefined) => void;
  onResetRange: () => void;
}) {
  const { t } = useTranslation();
  const selectRangeLabel = t('SELECT_RANGE');
  const rangeTo = selectedRange?.to ?? selectedRange?.from;
  const isNextDisabled = !rangeTo || !isBefore(startOfDay(rangeTo), startOfDay(today));

  // Moves the range by its own length, so the default 7 days steps a week. Next stops at today.
  const shiftRange = (direction: 1 | -1) => {
    if (!selectedRange?.from || !rangeTo) return;
    const length = differenceInCalendarDays(rangeTo, selectedRange.from) + 1;
    const to = direction === 1 ? min([addDays(rangeTo, length), today]) : subDays(rangeTo, length);
    onRangeSelect({ from: subDays(to, length - 1), to });
  };

  return (
    <Popover>
      {/* Anchor on the whole pill so the calendar lines up with its right edge, not the label's. */}
      <PopoverAnchor asChild>
        <div className="flex w-fit items-center gap-1 rounded-card border border-grey-100 bg-true-white p-px dark:border-border dark:bg-card sm:ml-auto">
          <button
            type="button"
            aria-label={t('PREVIOUS')}
            disabled={!selectedRange?.from}
            onClick={() => shiftRange(-1)}
            className={arrowClass}
          >
            <ChevronLeft className="size-4" />
          </button>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={t('DATE_RANGE')}
              className="whitespace-nowrap rounded-card px-1 text-sm font-medium leading-body text-grey-900 hover:text-grey-950 dark:text-foreground"
            >
              {formatRangeLabel(selectedRange, selectRangeLabel)}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto rounded-xl border-border p-0 shadow-lg">
            <div className="border-b border-border px-4 py-3">
              <div className="text-xs font-medium text-muted-foreground">{t('FILTER_PERIOD')}</div>
              <div className="mt-1 text-sm text-foreground">
                {periodStart && periodEnd
                  ? formatWindow(periodStart, periodEnd)
                  : formatRangeLabel(selectedRange, selectRangeLabel)}
              </div>
            </div>
            <Calendar
              mode="range"
              numberOfMonths={2}
              defaultMonth={selectedRange?.from ?? initialFrom}
              selected={selectedRange}
              onSelect={onRangeSelect}
              disabled={(date) => date > today}
              initialFocus
              className="p-3"
            />
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={onResetRange}
              >
                {t('RESET')}
              </Button>
              <div className="font-mono text-xs text-muted-foreground">
                {dayCount === 1
                  ? t('DAY_COUNT', { count: periodStart ? dayCount : 0 })
                  : t('DAYS_COUNT', { count: periodStart ? dayCount : 0 })}
              </div>
            </div>
          </PopoverContent>
          <button
            type="button"
            aria-label={t('NEXT')}
            disabled={isNextDisabled}
            onClick={() => shiftRange(1)}
            className={arrowClass}
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </PopoverAnchor>
    </Popover>
  );
}
